import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../lib/AuthProvider';
import { useTheme } from '../lib/useTheme';
import { STRIPE_PRODUCTS, ESCRITORIO_PATHS } from '../config/stripeConfig';
import TermsModal from '../lib/TermsModal';

const C = { turquesa:'#5dc1b9', mango:'#FDBE02', coral:'#eb636b', logoGrad:'linear-gradient(135deg, #534AB7, #1D9E75)' };
const font = "'DM Sans', sans-serif";
const serif = "'DM Serif Display', serif";

export default function PagoExitoso() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, profile, refreshProfile, membership } = useAuth();
  const t = useTheme();
  const [confetti, setConfetti] = useState([]);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [verified, setVerified] = useState(null); // null = loading, true/false

  const sessionId = searchParams.get('session_id');
  const membershipParam = searchParams.get('membership');
  const product = STRIPE_PRODUCTS[membershipParam] || null;
  const membershipName = product?.name || 'Thinker & Doer';
  const destPath = ESCRITORIO_PATHS[membershipParam] || '/sala';

  useEffect(() => {
    if (profile?.accepted_terms !== false || membership === 'admin') setTermsAccepted(true);
  }, [profile, membership]);

  // Verify session with Stripe
  useEffect(() => {
    if (!sessionId) { setVerified(true); return; }
    fetch('/api/verify-session', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ sessionId }),
    })
      .then(r => r.json())
      .then(d => setVerified(d.valid !== false))
      .catch(() => setVerified(true)); // Don't block on verification failure
  }, [sessionId]);

  useEffect(() => {
    // Generate confetti
    const pieces = Array.from({ length: 40 }, (_, i) => ({
      id: i,
      left: Math.random() * 100,
      delay: Math.random() * 2,
      color: [C.turquesa, C.mango, C.coral, '#534AB7', '#1D9E75'][i % 5],
      size: 6 + Math.random() * 8,
    }));
    setConfetti(pieces);

    // Refresh profile to pick up new membership
    if (user && refreshProfile) {
      setTimeout(() => refreshProfile(), 2000);
    }
  }, [user]);

  if (!termsAccepted && user) {
    return <TermsModal userId={user.id} onAccept={() => { setTermsAccepted(true); if (refreshProfile) refreshProfile(); }} />;
  }

  if (verified === null) {
    return (
      <div style={{ minHeight:'100vh', background:t.bg, fontFamily:font, color:t.text, display:'flex', alignItems:'center', justifyContent:'center' }}>
        <p style={{ fontSize:16, color:t.textSecondary }}>Verificando tu pago...</p>
      </div>
    );
  }

  return (
    <div style={{ minHeight:'100vh', background:t.bg, fontFamily:font, color:t.text, display:'flex', alignItems:'center', justifyContent:'center', position:'relative', overflow:'hidden' }}>
      <style>{`@keyframes fall{0%{transform:translateY(-100vh) rotate(0deg);opacity:1}100%{transform:translateY(100vh) rotate(720deg);opacity:0}}`}</style>

      {/* Confetti */}
      {confetti.map(p => (
        <div key={p.id} style={{
          position:'absolute', top:0, left:`${p.left}%`, width:p.size, height:p.size,
          background:p.color, borderRadius: p.id % 3 === 0 ? '50%' : '2px',
          animation:`fall ${2 + Math.random() * 3}s ease-in ${p.delay}s both`,
          zIndex:1,
        }} />
      ))}

      <div style={{ textAlign:'center', padding:'3rem', maxWidth:480, zIndex:2, background:'rgba(255,255,255,0.05)', backdropFilter:'blur(20px)', borderRadius:24, border:'1px solid rgba(255,255,255,0.1)' }}>
        <p style={{ fontSize:64, margin:'0 0 16px' }}>🎉</p>
        <h1 style={{ fontFamily:serif, fontSize:28, fontWeight:400, margin:'0 0 12px' }}>
          ¡Bienvenido a {membershipName}!
        </h1>
        <p style={{ fontSize:15, color:t.textSecondary, lineHeight:1.7, margin:'0 0 20px' }}>
          Tu pago fue exitoso. Ya tienes acceso completo.
        </p>

        {/* Genio message */}
        <div style={{ display:'flex', alignItems:'flex-start', gap:10, textAlign:'left', margin:'0 0 12px', padding:'12px 16px', borderRadius:14, background:'rgba(216,90,48,0.08)', border:'1px solid rgba(216,90,48,0.15)' }}>
          <span style={{ fontSize:20, flexShrink:0 }}>⚡</span>
          <p style={{ margin:0, fontSize:14, color:'#D85A30', lineHeight:1.5 }}>
            <strong>Genio:</strong> ¡Arriba! Ya estas dentro. Vamos a darle.
          </p>
        </div>

        {/* Luca message */}
        <div style={{ display:'flex', alignItems:'flex-start', gap:10, textAlign:'left', margin:'0 0 28px', padding:'12px 16px', borderRadius:14, background:'rgba(24,95,165,0.08)', border:'1px solid rgba(24,95,165,0.15)' }}>
          <span style={{ fontSize:20, flexShrink:0 }}>◆</span>
          <p style={{ margin:0, fontSize:14, color:'#185FA5', lineHeight:1.5 }}>
            <strong>Luca:</strong> Bienvenido. Aqui empieza el trabajo de verdad.
          </p>
        </div>

        <button onClick={() => navigate(destPath)} style={{
          background:C.logoGrad, color:'#fff', border:'none', borderRadius:50,
          padding:'16px 40px', fontSize:16, fontWeight:500, cursor:'pointer', fontFamily:font,
          boxShadow:'0 8px 24px rgba(93,193,185,0.3)', width:'100%', maxWidth:280,
        }}>
          Ir a mi escritorio →
        </button>

        <p style={{ fontSize:12, color:t.textSecondary, marginTop:20 }}>
          Si no ves tu membresia activa, espera unos segundos y recarga la pagina.
        </p>
      </div>
    </div>
  );
}
